import React from 'react';
import { Tooltip, OverlayTrigger } from '../ui-kit';
import { IconPlus, IconMinus, IconExclamationTriangle, IconEdit, IconBell, IconFileLines } from '../ui-kit/icons';
import DefaultReactTable, { timeColumn } from '../../utils/react-table';
import { formatTime } from '../../utils/time';
import StartButton from '../payroll-positions/StartButton';
import round from '../../utils/round';

const attrName = key => I18n.t(`activerecord.attributes.reporting/payroll_export/payroll_event_interval.${key}`);

const missingWarning = value => {
  if (value) return value;
  return (
    <>
      <IconExclamationTriangle /> {I18n.t('none')}
    </>
  );
};

const DateWithHoliday = ({ holiday, date }) => {
  if (holiday) {
    const tooltip = <Tooltip id="tooltip">{holiday}</Tooltip>;
    return (
      <OverlayTrigger placement="left" overlay={tooltip}>
        <abbr>
          <b>{date}</b>
        </abbr>
      </OverlayTrigger>
    );
  }
  return date;
};

// column definition for ReactTable
const columns = (adminRights, groupId) => {
  return [
    {
      accessor: 'delta',
      filterable: false,
      width: 25,
      Cell: row => ({ added: <IconPlus />, removed: <IconMinus /> })[row.value]
    },
    {
      Header: I18n.t('activerecord.models.user'),
      accessor: d => d.user.name,
      id: 'userName',
      Cell: row => {
        const { user } = row.original;
        const url = `/users/${user.id}`;
        return (
          <a href={url} target="_blank" rel="noreferrer">
            {row.value}
          </a>
        );
      }
    },
    {
      Header: I18n.t('attributes.type'),
      accessor: 'type',
      Cell: row => {
        if (!adminRights) return row.value;

        // Admins will get a link to the payroll rule that
        // generated this item
        const url = `/groups/${groupId}/payroll_rules/${row.original.rule_id}/edit?locale=en-GB`;

        return (
          <a href={url} target="_blank" rel="noreferrer">
            {row.value}
          </a>
        );
      }
    },
    {
      Header: I18n.t('attributes.details'),
      accessor: 'details',
      Cell: row => {
        const content = <span dangerouslySetInnerHTML={{ __html: row.value }} />;
        const translation = {
          activity: 'incidents/general_activity_report.one',
          incident: 'incidents/incident.one'
        }[row.original.event_type];
        if (!translation) return content;

        const tooltip = <Tooltip id="tooltip">{I18n.t(`activerecord.models.${translation}`)}</Tooltip>;
        const icon = { activity: <IconFileLines />, incident: <IconBell /> }[row.original.event_type];
        return (
          <OverlayTrigger placement="left" overlay={tooltip}>
            <div>
              {icon} {content}
            </div>
          </OverlayTrigger>
        );
      },
      filterable: true,
      width: 400
    },
    timeColumn({
      Header: I18n.t('attributes.start_time'),
      id: 'start_time',
      accessor: d => d.start_time,
      Cell: row => {
        const { holiday } = row.original;
        const formattedDate = formatTime(row.value);
        return <DateWithHoliday date={formattedDate} holiday={holiday} />;
      },
      width: 170
    }),
    timeColumn({
      Header: I18n.t('attributes.end_time'),
      id: 'end_time',
      accessor: d => d.end_time,
      Cell: row => {
        const { holiday } = row.original;
        const formattedDate = formatTime(row.value) || I18n.t('not_applicable_abbr');
        return <DateWithHoliday date={formattedDate} holiday={holiday} />;
      },
      width: 170
    }),
    {
      Header: attrName('role_number'),
      accessor: 'role_number',
      Cell: row => (
        <>
          {missingWarning(row.value)}
          <span className="float-right">
            <StartButton size="sm" label={<IconEdit />} membershipId={row.original.membership.id} />
          </span>
        </>
      ),
      width: 200
    },
    {
      Header: attrName('element_code'),
      accessor: 'element_code',
      width: 125
    },
    {
      Header: attrName('billable_hours'),
      id: 'billable_hours',
      className: 'text-end',
      accessor: d => round(d.billable_hours, 2),
      filterable: false,
      width: 50
    },
    {
      Header: I18n.t('activerecord.models.group'),
      id: 'group_name',
      // allows for searching group name and short code
      accessor: d => `${d.group.name} ${d.group.short_code}`,
      Cell: d => (
        <a target="_blank" href={`/groups/${d.original.group.id}`} rel="noreferrer">
          {d.original.group.short_code || d.original.group.name}
        </a>
      ),
      filterable: true
    },
    {
      Header: I18n.t('activerecord.attributes.group.cost_center'),
      id: 'cost_center',
      accessor: d => `${d.cost_center} ${d.group.name}`,
      Cell: row => {
        const { group } = row.original;
        const costCenterFormatted = missingWarning(row.original.cost_center);
        const url = `/groups/${group.id}`;
        const tooltip = <Tooltip id="tooltip">{group.name}</Tooltip>;
        return (
          <OverlayTrigger placement="left" overlay={tooltip}>
            <span>
              <a href={url} target="_blank" rel="noreferrer">
                {costCenterFormatted}
              </a>
            </span>
          </OverlayTrigger>
        );
      }
    }
  ];
};

const PayrollExportDetail = props => (
  <DefaultReactTable
    filterable
    showPaginationTop
    showPaginationBottom
    data={props.line_items}
    columns={columns(props.adminRights, props.groupId)}
    defaultSorted={[{ id: 'start_time' }]}
    defaultPageSize={50}
  />
);

export default PayrollExportDetail;
